import styled from '@emotion/styled';
import theme from '@ep/theme';
import useScroll from '@ep/utils/useScroll';
import loadable from '@loadable/component';
import React, { useEffect, useRef, useState } from 'react';
import { config } from 'react-spring';
const Carousel = loadable(() => import('react-spring-3d-carousel'));

export interface Carousel3DProps {
  children: JSX.Element[];
}

export interface slidesProps {
  key: number;
  content: JSX.Element;
}

export interface PageIndicatorProps {
  active: boolean;
  themeColor?: string;
}

const GalleryContainer = styled.div`
  height: min-content;
  width: 100%;
  display: flex;
  flex-direction: column;
  justify-content: center;
`;

const DesktopContainer = styled.div`
  ${theme.mediaQueries.desktop} {
    margin-top: 15rem;
  }
  ${theme.mediaQueries.mobileAndTablet} {
    display: none;
  }
`;

const MobileContainer = styled.div`
  max-width: 100%;
  margin-top: 1rem;
  background-color: white;
  display: flex;
  position: relative;
  scroll-snap-type: x mandatory;
  overflow-x: scroll;
  scrollbar-width: none;
  -ms-overflow-style: none;

  ::-webkit-scrollbar {
    display: none;
  }
  ${theme.mediaQueries.desktop} {
    display: none;
  }
`;

const MobileCard = styled.div`
  width: 100%;
  align-items: flex-start;
  scroll-snap-align: start;
  flex-shrink: 0;
  :not(:first-of-type) {
    margin-left: 0.5rem;
  }
`;

const CardIndicators = styled.div`
  margin-top: 9.5rem;
  background-color: white;
  display: flex;
  justify-content: center;
  ${theme.mediaQueries.tablet} {
    margin-top: 1rem;
  }
  ${theme.mediaQueries.mobile} {
    margin-top: 1rem;
  }
`;

const CardIndicator = styled.div<PageIndicatorProps>`
  margin: 0.5rem;
  width: 1rem;
  height: 1rem;
  border-radius: 50%;
  background-color: transparent;
  transition: ${theme.transitions.create(['background-color', 'border-color'])};
  cursor: pointer;
  ${({ themeColor }) =>
    `
    border: 1px solid ${
      themeColor && themeColor != theme.colors.white
        ? theme.colors.white
        : theme.colors.mediumBlue
    };
  `}

  ${({ active, themeColor }) =>
    active
      ? `
        background-color: ${
          themeColor && themeColor != theme.colors.white
            ? theme.colors.white
            : theme.colors.mediumBlue
        };

      `
      : `
        background-color: ${themeColor}
      `}
`;

const Carousel3D = ({ children }: Carousel3DProps) => {
  const [state, setState] = useState({
    goToSlide: 0,
    offsetRadius: 4,
    showNavigation: false,
    config: config.gentle,
  });
  const [pageIndicator, setPageIndicator] = useState(0);

  const postsRef = useRef<HTMLDivElement>(null);
  const { x } = useScroll(postsRef);
  const currentIndex = postsRef.current
    ? Math.round(x / postsRef.current.clientWidth)
    : 0;

  useEffect(() => {
    setPageIndicator(currentIndex);
  }, [currentIndex]);

  const scrollTo = (index: number) => {
    postsRef.current?.scroll({
      left: (postsRef.current.children[index] as HTMLElement).offsetLeft,
      behavior: 'smooth',
    });
  };

  const slides: slidesProps[] = [];

  children.forEach((child: JSX.Element, index) => {
    slides.push({
      key: index,
      content: child,
    });
  });

  return (
    <GalleryContainer>
      <DesktopContainer>
        <Carousel
          slides={slides}
          goToSlide={state.goToSlide}
          offsetRadius={state.offsetRadius}
          showNavigation={state.showNavigation}
          animationConfig={state.config}
        />
      </DesktopContainer>

      <MobileContainer ref={postsRef} tabIndex={0}>
        {children &&
          children.map((child: JSX.Element) => (
            <MobileCard>{child}</MobileCard>
          ))}
      </MobileContainer>
      <CardIndicators>
        {children &&
          children.length > 1 &&
          children.map((slide, index) => (
            <CardIndicator
              key={slide.key}
              active={pageIndicator === index}
              onClick={() => {
                setPageIndicator(index);
                setState(prev=>({ ...prev, goToSlide: index }));
                scrollTo(index);
              }}
            />
          ))}
      </CardIndicators>
    </GalleryContainer>
  );
};

export default Carousel3D;
